import {
  EmissionsHomeViewFilterAreaSchemaKeys,
  type EmissionsHomeViewFilterAreaSchemaType,
} from "#src/batteries-included-components/FilterAreas/DashboardsFilterAreas/DashboardsFilterAreas";
import { useMeasurementTypes } from "#src/contexts/MeasurementTypeContext";
import { useStickyState } from "#src/hooks/useStickyState";
import { getIntervalInMonthsFormatted, hydrateDateRange } from "#utils/date";
import { useQuery } from "@tanstack/react-query";
import {
  Panel,
  PieChart,
  StorageKeys,
  useToast,
} from "@validereinc/common-components";
import { CalculatorResultsDomain } from "@validereinc/domain";
import { Measurements } from "@validereinc/domain-controllers";
import { formatNumberValueAndUnit } from "@validereinc/utilities";
import classNames from "classnames/bind";
import React, { useMemo } from "react";
import styles from "./PollutantPieChartPanel.module.scss";
import { getTopNumberList } from "./top-number-list-util";

const { PollutantsEmitted } = Measurements.logic;

type DataPoint = {
  label: string;
  value: number;
};

const cx = classNames.bind(styles);
const defaultTopListConfig = { top: 10, hasOther: true };
const pollutants = Object.values(PollutantsEmitted);

export const PollutantPieChartPanel = ({
  viewConfigStorageKey,
}: Pick<StorageKeys, "viewConfigStorageKey">) => {
  const { toast } = useToast();
  const { getUnitByType, getPrecisionByType, getUnitName } =
    useMeasurementTypes();
  const [
    {
      [EmissionsHomeViewFilterAreaSchemaKeys.date_range]: dateRange,
      [EmissionsHomeViewFilterAreaSchemaKeys.pollutant]: pollutant,
      [EmissionsHomeViewFilterAreaSchemaKeys.reporting_scenario]:
        reportingGroup,
    },
  ] = useStickyState<EmissionsHomeViewFilterAreaSchemaType>(
    {},
    viewConfigStorageKey
  );

  const { mass, label } = pollutant
    ? (PollutantsEmitted[pollutant] ?? PollutantsEmitted.co2_eq)
    : PollutantsEmitted.co2_eq;
  const precision = getPrecisionByType(mass);
  const unit = getUnitName(getUnitByType(mass));

  const onError = () =>
    toast.push({
      intent: "error",
      description:
        "There was an error fetching your emissions by equipment type",
    });

  const request = {
    sortBy: `measurement.${mass}`,
    sortDirection: "desc",
    pageSize: 25,
    filters: {
      measurement_type: {
        $in: ["mass_ch4", "mass_co2", "mass_co2eq", "mass_n2o"],
      },
      reporting_group: reportingGroup,
      year_month: {
        // @ts-expect-error TODO: date format is not correct
        $in: getIntervalInMonthsFormatted(hydrateDateRange(dateRange)),
      },
      isAlreadyFormatted: true,
    },
    groupBy: ["equipment.type.name"],
  };

  const { data } = useQuery({
    queryKey: ["calculatorResults", request],
    queryFn: () => CalculatorResultsDomain.getList(request as any),
    select: ({ data }) => data,
    enabled: !!reportingGroup && !!dateRange,
    onError,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const equipmentEmissions = useMemo(() => {
    if (!data) {
      return [];
    }
    const patchedData = data.map((record) => {
      pollutants.forEach(({ mass }) => {
        if (!record.measurement[mass]) {
          record.measurement[mass] = 0;
        }
      });

      return record;
    });
    const filteredData = getTopNumberList(
      patchedData,
      defaultTopListConfig.top,
      defaultTopListConfig.hasOther
    );
    return filteredData.map((record) => ({
      label: record["equipment.type.name"],
      value: record.measurement[mass],
    }));
  }, [data, pollutant]);

  const title = `Emissions Split by ${label}`;

  return (
    <Panel title={title}>
      <div className={cx("pieChart")}>
        <PieChart
          // @ts-expect-error invalid types
          data={equipmentEmissions as any}
          labelAccessor={({ label }: DataPoint) => label}
          valueAccessor={({ value }: DataPoint) => value}
          valueFormatter={(value: number) =>
            formatNumberValueAndUnit(
              { value, unit },
              { overrideOpts: { minimumFractionDigits: precision ?? 0 } }
            ) ?? "-"
          }
          orientation="vertical"
          parentWidth={300}
          isPieChartCentered
        />
      </div>
    </Panel>
  );
};
